import React from 'react';
import { Facebook, Phone, Mail, MapPin } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { companyData, logoUrl } from '../data';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (e, href) => {
    e.preventDefault();
    if (href === '/kontakt' || href === '/galleri') {
      navigate(href);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (location.pathname === '/') {
      const targetId = href === '/' ? '#forside' : href.replace('/', '');
      const element = document.querySelector(targetId);
      if (element) {
        const offset = 80;
        const bodyRect = document.body.getBoundingClientRect().top;
        const elementRect = element.getBoundingClientRect().top;
        const elementPosition = elementRect - bodyRect;
        const offsetPosition = elementPosition - offset;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth'
        });
      }
    } else {
      navigate(href);
    }
  };

  return (
    <footer className="bg-white border-t border-gray-200 text-gray-600 text-sm">
      {/* Upper Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          
          {/* Brand Column */}
          <div className="md:col-span-5 space-y-4">
            <a href="/" onClick={(e) => handleNavClick(e, '/')} className="inline-block">
              <img
                src={logoUrl}
                alt={companyData.name}
                className="h-20 md:h-24 w-auto object-contain"
              />
            </a>
            <p className="text-gray-500 text-xs leading-relaxed max-w-sm">
              Bust Byg & Renovering ApS leverer professionelt tømrer-, murer-, VVS- og renoveringsarbejde på hele Sjælland. Vi går op i kvalitet, ordentlighed og holdbare løsninger.
            </p>
            {/* Social Icons */}
            <div className="flex items-center gap-3 pt-2">
              <a
                href="https://www.facebook.com" // Placeholder for Facebook page
                target="_blank"
                rel="noopener noreferrer"
                className="text-black hover:text-orange-500 transition-colors"
                aria-label="Følg os på Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links Column */}
          <div className="md:col-span-3 space-y-4">
            <h4 className="text-gray-900 font-bold uppercase tracking-wider text-xs">Hurtige links</h4>
            <ul className="space-y-2.5 text-xs">
              <li>
                <a href="/" onClick={(e) => handleNavClick(e, '/')} className="hover:text-black transition-colors">
                  Forside
                </a>
              </li>
              <li>
                <a href="/#om-os" onClick={(e) => handleNavClick(e, '/#om-os')} className="hover:text-black transition-colors">
                  Om os
                </a>
              </li>
              <li>
                <a href="/galleri" onClick={(e) => handleNavClick(e, '/galleri')} className="hover:text-black transition-colors">
                  Projektgalleri
                </a>
              </li>
              <li>
                <a href="/kontakt" onClick={(e) => handleNavClick(e, '/kontakt')} className="hover:text-black transition-colors">
                  Kontakt
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Details Column */}
          <div className="md:col-span-4 space-y-4">
            <h4 className="text-gray-900 font-bold uppercase tracking-wider text-xs">Kontaktinfo</h4>
            <ul className="space-y-3 text-xs">
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 text-black shrink-0 mt-0.5" />
                <a href={`tel:${companyData.phoneRaw}`} className="hover:text-black transition-colors">
                  {companyData.phone}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 text-black shrink-0 mt-0.5" />
                <a href={`mailto:${companyData.email}`} className="hover:text-black transition-colors break-all">
                  {companyData.email}
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 text-black shrink-0 mt-0.5" />
                <a
                  href={companyData.addressMapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-black transition-colors"
                >
                  {companyData.address}
                </a>
              </li>
            </ul>
          </div>

        </div>
      </div>

      {/* Lower Footer */}
      <div className="bg-gray-50 py-6 border-t border-gray-200 text-center text-xs text-gray-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p>© {currentYear} {companyData.name}. Alle rettigheder forbeholdes.</p>
          <div className="flex items-center gap-4">
            <p>CVR: 42389104 {/* Realistic placeholder CVR */}</p>
            <span className="text-gray-300">|</span>
            <p>Udviklet med fokus på kvalitet</p>
          </div>
        </div>
      </div>
    </footer>
  );
}
